import { zodResolver } from '@hookform/resolvers/zod';
import { Memo } from '@kalos/kalos-rpc';
import {
  Button,
  DialogFooter,
  FileUploader,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  LoadingIcon,
  toast,
} from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { DialogOrSheet } from '../../../../../components/DialogOrSheet';
import { queryKeys } from '../../../../../hooks/react-query/constants';
import {
  useAddFilesToMemoMutation,
  useUpdateMemoMutation,
} from '../../../../../hooks/react-query/useMemoQuery';
import {
  isVideoFile,
  shouldUseMultipartUpload,
  useLargeFileUploadMutation,
} from '../../../../../hooks/react-query/useS3';
import { useFileValidation } from '../../../../../hooks/useFileValidation';
import { useMobileDevice } from '../../../../../hooks/useMedia';
import {
  FILE_UPLOAD_ACCEPT,
  generateUniqueFileName,
  isValidFileType,
  MAX_FILE_SIZE_BYTES,
  MAX_FILES,
  mergeFileIdCsv,
} from '../../../../../tools/fileUploadUtils';
import { MemoClientService } from '../../../../../tools/helpers';

const formSchema = z.object({
  files: z
    .array(z.instanceof(File))
    .min(1, 'At least one file is required')
    .max(MAX_FILES, `Cannot upload more than ${MAX_FILES} files`),
});

type FormValues = z.infer<typeof formSchema>;

interface AddMemoFileFormProps {
  memoId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AddMemoFileForm: FC<AddMemoFileFormProps> = ({
  memoId,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const isMobileDevice = useMobileDevice();
  const { mutateAsync: addFiles } = useAddFilesToMemoMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.memoList.root],
      });
    },
  });

  const { mutateAsync: uploadLargeFile, isPending: isLargeUploading } =
    useLargeFileUploadMutation();

  const { mutateAsync: updateMemo } = useUpdateMemoMutation();

  const [isUploadingLargeVideos, setIsUploadingLargeVideos] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      files: [],
    },
  });

  const watchedFiles = form.watch('files');

  // Shared real-time file validation
  const { validationError } = useFileValidation(form, 'files', watchedFiles);

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      // Validate each file's type and size
      const validationResults = await Promise.all(
        values.files.map(async (file) => ({
          file,
          isValid: (await isValidFileType(file)) && file.size <= MAX_FILE_SIZE_BYTES,
        })),
      );

      const validFiles = validationResults.filter((r) => r.isValid).map((r) => r.file);
      const invalidFiles = validationResults.filter((r) => !r.isValid).map((r) => r.file);

      if (validFiles.length === 0) {
        toast({
          title: 'Upload Failed',
          description:
            'Invalid file type or size detected. Allowed types: JPEG, PNG, WEBP, HEIC (\u2264150 MB each) and MP4, WEBM, MOV (\u2264150 MB each).',
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }

      if (invalidFiles.length > 0) {
        toast({
          title: 'Some files skipped',
          description: `${invalidFiles.length} file(s) were skipped due to unsupported file type.`,
          variant: 'destructive',
        });
      }

      // Separate valid files into regular and large videos
      const regularFiles: File[] = [];
      const largeVideoFiles: File[] = [];

      validFiles.forEach((file) => {
        if (isVideoFile(file) && shouldUseMultipartUpload(file)) {
          largeVideoFiles.push(file);
        } else {
          regularFiles.push(file);
        }
      });

      // Regular files: the mutation stages images and assigns unique names.
      if (regularFiles.length > 0) {
        await addFiles({ memoId, files: regularFiles });
      }

      // Fetch current memo to get latest fileId csv after regular upload
      const latestMemo = await MemoClientService.Get(Memo.create({ id: memoId }));
      let existingFileIdCsv = latestMemo?.fileId || '';

      // Handle large videos
      if (largeVideoFiles.length > 0) {
        setIsUploadingLargeVideos(true);

        const uploadedIds: number[] = [];

        for (const file of largeVideoFiles) {
          const uniqueName = generateUniqueFileName(file.name);

          const uploaded = await uploadLargeFile({
            file,
            bucket: 'testbuckethelios',
            key: uniqueName,
          });

          if (uploaded?.id) {
            uploadedIds.push(uploaded.id);
          }
        }

        setIsUploadingLargeVideos(false);

        if (uploadedIds.length > 0) {
          const mergedCsv = mergeFileIdCsv(existingFileIdCsv, uploadedIds);

          await updateMemo(Memo.create({ id: memoId, fileId: mergedCsv, fieldMask: ['FileId'] }));

          existingFileIdCsv = mergedCsv;
        }
      }

      toast({
        title: 'Files added successfully',
        description: `${validFiles.length} file(s) have been added to the note`,
      });

      form.reset();
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast({
        title: 'Error uploading files',
        description: 'There was an error uploading one or more files. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitDisabled =
    isSubmitting || isUploadingLargeVideos || isLargeUploading || !!validationError;

  const uploaderDisabled = isSubmitting || isUploadingLargeVideos || isLargeUploading;

  return (
    <DialogOrSheet
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title="Add Files to Note"
      isBusy={uploaderDisabled}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="files"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Files</FormLabel>
                <FormControl>
                  <FileUploader
                    value={field.value}
                    onValueChange={field.onChange}
                    maxFiles={MAX_FILES}
                    multiple
                    maxSize={MAX_FILE_SIZE_BYTES}
                    accept={FILE_UPLOAD_ACCEPT}
                    disabled={uploaderDisabled}
                    // Phones have no drag and drop: Take Photo and Browse Files instead.
                    useButtons={isMobileDevice}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitDisabled}>
              {isSubmitting || isUploadingLargeVideos || isLargeUploading ? (
                <div className="flex items-center gap-2">
                  <LoadingIcon className="h-4 w-4" />
                  <span>Uploading...</span>
                </div>
              ) : (
                'Upload Files'
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogOrSheet>
  );
};
